import { useQuery } from "@tanstack/react-query";

import { apiClient } from "../../api/apiClient";
import {
  getAllFilesApiV1FilesGet,
  getFileApiV1FilesIdGet,
  getFileSasUrlApiV1FilesFileIdSasUrlGet,
  getFileUploadSasUrlApiV1FilesUploadSasUrlPost,
} from "../../api/generated/sdk.gen";
import {
  DocSorterFileSchema,
  type FileSasUrlResponseSchema,
  type FileUploadSasUrlRequestSchema,
  type FileUploadSasUrlResponseSchema,
  GetAllFilesApiV1FilesGetData,
} from "../../api/generated/types.gen";
import { CLIENT_KEYS } from "../../constants/clientKeys";
import { FileWithIndexing } from "../../types/file-indexing";

const FAST_POLL_MS = 10 * 1000; // 10s — files mid-index
const SLOW_POLL_MS = 5 * 60 * 1000; // 5 min — steady state

const hasFilesIndexing = (files: DocSorterFileSchema[] | undefined): boolean => {
  if (!files) return false;
  return files.some((f) => {
    const aug = f as FileWithIndexing;
    if (aug.indexing_status) {
      return (
        aug.indexing_status === "PENDING" || aug.indexing_status === "IN_PROGRESS"
      );
    }
    // Fallback for old backend without indexing_status: rely on is_indexed +
    // file status COMPLETED to infer the file should be indexed but isn't yet.
    return f.status === "COMPLETED" && aug.is_indexed === false;
  });
};

const hasFilesProcessing = (files: DocSorterFileSchema[] | undefined): boolean => {
  if (!files) return false;
  return files.some(
    (f) => f?.status === "PROCESSING" || f?.status === "TO_BE_PROCESSED"
  );
};

/**
 * Fetch a read-only, file-scoped SAS URL for one blob (generated SDK + `apiClient`).
 * For imperative flows such as preview, download, or `getFileUrl`; not cached by React Query.
 *
 * @param fileId - The doc sorter file id
 * @returns `{ url }` — full blob URL including read-only SAS token
 * @throws Error if `fileId` is falsy, or if the request fails or returns no data
 */
export async function fetchFileSasUrl(
  fileId: string | undefined
): Promise<FileSasUrlResponseSchema> {
  if (!fileId) {
    // Fail loud on the client instead of shipping a literal `{file_id}` path
    // param, which the backend rejects as a raw 500 (MDRS-137).
    throw new Error("Failed to fetch file SAS URL: missing file id");
  }

  const response = await getFileSasUrlApiV1FilesFileIdSasUrlGet({
    client: apiClient,
    path: { file_id: fileId },
  });

  if ("error" in response && response.error) {
    throw new Error(
      `Failed to fetch file SAS URL: ${
        response.error.detail || JSON.stringify(response.error)
      }`
    );
  }

  if (!response.data) {
    throw new Error("Failed to fetch file SAS URL: empty response");
  }

  return response.data;
}

/**
 * Fetch all files with optional filtering
 * @param case_number - Filter by case number (required for files endpoint)
 * @param status - Filter by status (comma-separated list)
 * @param classification - Filter by classification
 * @param created_at - Filter by created at date
 * @param issue_date - Filter by issue date
 * @param sort_by - Sort order: recently_added, recently_updated, or oldest
 * @param enabled - Whether the query should run
 * @returns Query result with files data
 */
export const useFiles = ({
  case_number,
  status,
  classification,
  created_at,
  issue_date,
  sort_by,
  enabled = true,
}: {
  enabled?: boolean;
} & GetAllFilesApiV1FilesGetData["query"] = {}) => {
  return useQuery({
    queryKey: [
      CLIENT_KEYS.FILES,
      {
        case_number,
        status,
        classification,
        created_at,
        issue_date,
        sort_by,
      },
    ],
    queryFn: async () => {
      const response = await getAllFilesApiV1FilesGet({
        client: apiClient,
        query: {
          case_number,
          status,
          classification,
          created_at,
          issue_date,
          sort_by,
        },
      });

      // Check for errors in the response
      if ("error" in response && response.error) {
        throw new Error(
          `Failed to fetch files: ${response.error.detail || JSON.stringify(response.error)}`
        );
      }

      return response.data;
    },
    enabled: enabled && !!case_number, // Files endpoint requires case_number
    refetchInterval: (query) => {
      // Poll fast while either the AI summary generator is still working OR
      // any file is mid-index, so the UI flips to COMPLETED / INDEXED quickly.
      // Otherwise fall back to a relaxed background refresh.
      const data = query.state.data as DocSorterFileSchema[] | undefined;
      return hasFilesProcessing(data) || hasFilesIndexing(data)
        ? FAST_POLL_MS
        : SLOW_POLL_MS;
    },
  });
};

/**
 * Fetch a single file by ID
 * @param fileId - The ID of the file to fetch
 * @param enabled - Whether the query should run
 * @returns Query result with file data
 */
export const useFile = ({
  fileId,
  enabled = true,
}: {
  fileId: string;
  enabled?: boolean;
}) => {
  return useQuery({
    queryKey: [CLIENT_KEYS.FILES, fileId],
    queryFn: async () => {
      const response = await getFileApiV1FilesIdGet({
        path: { id: fileId },
        client: apiClient,
      });

      // Check for errors in the response
      if ("error" in response && response.error) {
        throw new Error(
          `Failed to fetch file: ${response.error.detail || JSON.stringify(response.error)}`
        );
      }

      return response.data;
    },
    enabled: enabled && !!fileId,
  });
};

/**
 * Fetch a write-only SAS URL for a single blob targeted at the upload path.
 * Backend validates the case belongs to the requesting user before issuing.
 */
export async function fetchFileUploadSasUrl(
  payload: FileUploadSasUrlRequestSchema
): Promise<FileUploadSasUrlResponseSchema> {
  const response = await getFileUploadSasUrlApiV1FilesUploadSasUrlPost({
    client: apiClient,
    body: payload,
  });

  if ("error" in response && response.error) {
    throw new Error(
      `Failed to fetch upload SAS URL: ${
        response.error.detail || JSON.stringify(response.error)
      }`
    );
  }

  if (!response.data) {
    throw new Error("Failed to fetch upload SAS URL: empty response");
  }

  return response.data;
}

/**
 * Fetch all files for a specific case
 * This is a simpler version of useFiles specifically for getting all files for a case
 * without needing to provide other filters
 *
 * @param case_number - The case number to fetch files for
 * @param enabled - Whether the query should run
 * @returns Query result with exported files data
 */
export const useAllCaseFiles = ({
  case_number,
  enabled = true,
}: {
  case_number: string;
  enabled?: boolean;
}) => {
  return useQuery({
    queryKey: [CLIENT_KEYS.FILES, case_number],
    queryFn: async () => {
      const response = await getAllFilesApiV1FilesGet({
        client: apiClient,
        query: {
          case_number,
        },
      });

      // Check for errors in the response
      if ("error" in response && response.error) {
        throw new Error(
          `Failed to fetch exported files: ${
            response.error.detail || JSON.stringify(response.error)
          }`
        );
      }

      return response.data;
    },
    enabled: enabled && !!case_number,
  });
};
